import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Portal } from "react-portal";
import Swiper from "swiper";
import { EffectCube } from "swiper/modules";
import Stories from "./Stories.tsx";
import Story from "./Story.tsx";
import StoriesSlider from "./StoriesSlider.tsx";

import { useStorySeenMutation } from "@/pages/home/api";
import "./stories-slider.css";

import "swiper/css";
import "swiper/css/effect-cube";
import {
  AnimatePresence,
  motion,
  useAnimationControls,
  useMotionValue,
} from "motion/react";
import { useScrollLock } from "usehooks-ts";
import ImageStory from "./ImageStory.tsx";
import {
  TaskAwardResponse,
  TaskList,
  TaskStatusResponse,
} from "@/pages/get-don/tasks.types.ts";

interface StoriesProps {
  storiesData: any[];
  task?: TaskList;
  getAwardFn?: () => Promise<TaskAwardResponse>;
  claimAwardFn?: () => Promise<TaskStatusResponse>;
  setLoading?: (isLoading: boolean) => void;
}

const TaskStoryModal = forwardRef<any, StoriesProps>((props, ref) => {
  const { storiesData } = props;
  const [isOpen, setIsOpen] = useState(false);
  const storiesSliderRef = useRef<any>(null);

  useScrollLock({
    autoLock: isOpen,
  });

  const handleGetDon = async () => {
    if (!props.task) return;
    if (!props.claimAwardFn) return;

    const res = await props.claimAwardFn();
    if (res === "COMPLETED" || res === "READY_AWARD") {
      props.getAwardFn?.();
    }
  };

  const closeStories = () => {
    setIsOpen(false);
  };

  const animationControls = useAnimationControls();
  const y = useMotionValue(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const openStories = (userIndex: number) => {
    setIsOpen(true);
    y.set(0);
    setTimeout(() => {
      if (storiesSliderRef.current) {
        storiesSliderRef.current.el.classList.add("stories-slider-in");
        storiesSliderRef.current.enable();
        storiesSliderRef.current.slideTo(userIndex, 0);
      }
    });
  };

  useImperativeHandle(ref, () => ({
    openStories,
  }));

  const onCloseButtonClick = useCallback(() => {
    closeStories();
    if (storiesSliderRef.current) {
      storiesSliderRef.current.disable();
      storiesSliderRef.current.el.classList.add("stories-slider-out");
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      animationControls.start({
        opacity: 1,
        scale: 1,
      });
    }
  }, [isOpen, animationControls]);

  useEffect(() => {
    if (storiesSliderRef.current) {
      storiesSliderRef.current.el.addEventListener("animationend", () => {
        if (
          storiesSliderRef.current.el.classList.contains("stories-slider-out")
        ) {
          storiesSliderRef.current.el.classList.remove("stories-slider-in");
          storiesSliderRef.current.el.classList.remove("stories-slider-out");
        }
      });
    }

    return () => {
      animationControls.stop();
    };
  }, [animationControls]);

  const onSlidesIndexesChange = (mainIndex: number, subIndex: number) => {
    if (
      storiesData.length - 1 === mainIndex - 1 &&
      storiesData[mainIndex].stories.length - 1 === subIndex - 1
    ) {
      setIsOpen(false);
    }
  };

  const storySeenMutation = useStorySeenMutation();

  const storiesSeen = useCallback(
    (index: number) => {
      if (!storiesData[index].is_seen) {
        props.setLoading?.(true);
        storySeenMutation.mutateAsync(storiesData[index].id).then(() => {
          if (props.task) {
            handleGetDon().finally(() => {
              props.setLoading?.(false);
            });
          }
        });
      }
    },
    [storiesData, storySeenMutation, props.task]
  );

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <Portal>
            <motion.div
              ref={containerRef}
              key="stories-modal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={animationControls}
              exit={{
                opacity: 0,
                scale: 0.9,
                transition: { duration: 0.2 },
              }}
              transition={{ duration: 0.2 }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              className="fixed inset-0 z-10 bg-dark !touch-none [&_.stories-slider-button-next]:!hidden"
              dragListener={false} // We'll control drag manually
            >
              {/* 
                <div
                  className="absolute bg-red !touch-none top-0 left-1/2 -translate-x-1/2 w-2/3 h-[10rem] z-[10] cursor-grab active:cursor-grabbing"
                  onPointerDown={(e) => {
                    controls.start(e);
                    document.body.style.overflow = "hidden";
                  }}
                />
              */}
              <StoriesSlider
                swiper={Swiper}
                effectCube={EffectCube}
                enabled={false}
                autoplayDuration={5000}
                onSlidesIndexesChange={onSlidesIndexesChange}
                onStoriesSlider={(instance) =>
                  (storiesSliderRef.current = instance)
                }
                closeModal={onCloseButtonClick}
                storiesSeen={storiesSeen}
              >
                {storiesData.length > 0 &&
                  storiesData.map((userStories, userStoriesIndex) => (
                    <Stories key={userStoriesIndex}>
                      <>
                        {(userStories.slides as any[]).map(
                          (story, storyIndex) => (
                            <Fragment key={storyIndex}>
                              <Story>
                                {{
                                  content: story.video ? (
                                    <video
                                      src={story.video}
                                      playsInline
                                      preload="metadata"
                                    />
                                  ) : (
                                    <ImageStory image={story.image} />
                                  ),
                                }}
                              </Story>
                            </Fragment>
                          )
                        )}
                      </>
                    </Stories>
                  ))}
              </StoriesSlider>
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </>
  );
});

export default TaskStoryModal;
